import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Loader2, X } from 'lucide-react';
import { analystApi, type DatasetProfileView, type DatasetRowView, type StoryView } from '../../services/api';

/** Pannello dataset (§13.1): profilo e qualità, esploratore di righe e storie. */

const PAGE = 25;
const SEVERITY_STYLE: Record<string, string> = {
  SEVERE: 'border-red-700 bg-red-900/30 text-red-100',
  MATERIAL: 'border-amber-700 bg-amber-900/20 text-amber-100',
  INFO: 'border-gray-700 bg-gray-800/40 text-gray-300',
};

export function DatasetPanel({ datasetId, onClose }: { datasetId: string; onClose: () => void }) {
  const [profile, setProfile] = useState<DatasetProfileView | null>(null);
  const [rows, setRows] = useState<DatasetRowView[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({ channel: '', sentiment: '', source: '', q: '' });
  const [story, setStory] = useState<StoryView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    analystApi.datasetProfile(datasetId).then(setProfile).catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, [datasetId]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { limit: String(PAGE), offset: String(offset), onTopic: 'true' };
      for (const [k, v] of Object.entries(filters)) if (v.trim() !== '') params[k] = v.trim();
      const r = await analystApi.datasetRows(datasetId, params);
      setRows(r.rows);
      setTotal(r.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [datasetId, filters, offset]);

  useEffect(() => { void loadRows(); }, [loadRows]);

  const openStory = async (storyId: string) => {
    try {
      setStory(await analystApi.story(datasetId, storyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const setFilter = (key: keyof typeof filters, value: string) => { setOffset(0); setFilters({ ...filters, [key]: value }); };
  const flags = profile?.profile?.flags ?? [];

  return (
    <aside className="fixed inset-y-0 right-0 w-full max-w-2xl bg-gray-900 border-l border-gray-700 shadow-2xl z-40 overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 sticky top-0 bg-gray-900">
        <h3 className="text-sm font-semibold text-white">Dataset dell'analisi</h3>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Chiudi"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-4">
        {error && <p className="text-xs text-red-300">{error}</p>}
        {profile && (
          <div className="text-xs text-gray-300 space-y-2">
            <p>
              {profile.origin === 'export' ? 'Export caricato' : `${profile.searchName ?? 'Search'} (ID: ${profile.searchId})`} ·{' '}
              {profile.rows.toLocaleString('it-IT')} righe{profile.fetchMode === 'stratified_sample' ? ` (campione di ${profile.totalAvailable?.toLocaleString('it-IT')})` : ''}
              {profile.profile ? ` · ${profile.profile.unique_stories.toLocaleString('it-IT')} storie` : ''} ·{' '}
              {profile.window[0]?.slice(0, 16).replace('T', ' ')} → {profile.window[1]?.slice(0, 16).replace('T', ' ')} UTC · qualità {profile.quality ?? '—'}
            </p>
            {profile.keywords && profile.keywords.length > 0 && <p className="text-gray-500">Parole chiave del tema: {profile.keywords.join(', ')}</p>}
            <div className="space-y-1">
              {[...flags].sort((a, b) => ['SEVERE', 'MATERIAL', 'INFO'].indexOf(a.severity) - ['SEVERE', 'MATERIAL', 'INFO'].indexOf(b.severity)).map((f) => (
                <div key={f.code} className={`border rounded px-2 py-1 ${SEVERITY_STYLE[f.severity] ?? SEVERITY_STYLE['INFO']}`}>
                  <span className="font-semibold">{f.severity} · {f.code}</span>: {f.message}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select value={filters.channel} onChange={(e) => setFilter('channel', e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white">
            <option value="">Tutti i canali</option>
            <option value="organic_conversation">Conversazione organica</option>
            <option value="earned_media">Earned media</option>
            <option value="other">Altro</option>
          </select>
          <select value={filters.sentiment} onChange={(e) => setFilter('sentiment', e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white">
            <option value="">Tutti i sentiment</option>
            <option value="positive">positivo</option>
            <option value="neutral">neutro</option>
            <option value="negative">negativo</option>
            <option value="not_evaluable">non valutabile</option>
          </select>
          <input value={filters.source} onChange={(e) => setFilter('source', e.target.value)} placeholder="Fonte (es. FORUM)" className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white" />
          <input value={filters.q} onChange={(e) => setFilter('q', e.target.value)} placeholder="Cerca nel testo" className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white" />
        </div>

        <div className="text-xs text-gray-500 flex items-center gap-2">
          {loading && <Loader2 className="w-3 h-3 animate-spin" />}
          {total.toLocaleString('it-IT')} righe pertinenti · {offset + 1}–{Math.min(offset + PAGE, total)}
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="ml-auto px-2 py-0.5 border border-gray-700 rounded disabled:opacity-40">←</button>
          <button type="button" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} className="px-2 py-0.5 border border-gray-700 rounded disabled:opacity-40">→</button>
        </div>

        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.pulsar_id} className="bg-gray-800/40 border border-gray-800 rounded p-2 text-xs">
              <div className="flex flex-wrap gap-x-2 text-gray-400">
                <span className="text-gray-200">{r.author_kind === 'outlet' ? r.author_key : `@${r.author_key ?? '?'}`}</span>
                <span>{r.source}</span>
                <span>{r.date_utc.slice(0, 16).replace('T', ' ')}</span>
                <span>{r.sentiment_class}</span>
                {r.voice_type === 'publisher_on_social' && <span className="text-orange-300">editore sui social</span>}
                {r.quote_status === 'unresolved' && <span className="text-orange-300">citazione non risolta</span>}
                {r.story_size > 1 && (
                  <button type="button" onClick={() => openStory(r.story_id)} className="text-sky-300 hover:underline">storia {r.story_id} ({r.story_size} righe{r.story_wire ? `, ${r.story_wire}` : ''})</button>
                )}
                {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-300 inline-flex items-center gap-0.5">link <ExternalLink className="w-3 h-3" /></a>}
              </div>
              {r.title && r.conversation_channel === 'earned_media' && <p className="text-gray-200 mt-1">{r.title}</p>}
              <p className="text-gray-300 mt-1 line-clamp-3">{r.text}</p>
            </li>
          ))}
        </ul>

        {story && (
          <div className="bg-gray-950 border border-gray-700 rounded p-3 text-xs text-gray-300 space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-white">Storia {story.storyId}</span>
              <button type="button" onClick={() => setStory(null)} className="text-gray-400 hover:text-white" aria-label="Chiudi la storia"><X className="w-3.5 h-3.5" /></button>
            </div>
            <p>{story.rows} righe{story.wire ? `, lancio ${story.wire}` : ''}, {story.outlets.length} testate e {story.accounts.length} account, dal {story.firstSeen.slice(0, 16).replace('T', ' ')} al {story.lastSeen.slice(0, 16).replace('T', ' ')} UTC</p>
            {story.titles[0] && <p className="text-gray-200">«{story.titles[0]}»</p>}
            <p className="text-gray-500">Testate: {story.outlets.slice(0, 30).join(', ')}{story.outlets.length > 30 ? '…' : ''}</p>
          </div>
        )}
      </div>
    </aside>
  );
}
